/**
 * Burndown Chart Component
 * Shows contract amount vs actual costs over time (budget burndown)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import type { MonthlyData } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { TimePeriodToggle } from './TimePeriodToggle'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import { aggregateDataByTimePeriod, formatPeriodForExport, type TimePeriod } from '@/lib/utils/dashboard-utils'
import { useState, useMemo } from 'react'

interface BurndownChartProps {
  data: MonthlyData[]
  contractAmount: number
  projectedCost: number
  engagementId?: string | number
}

export function BurndownChart({ data, contractAmount, projectedCost, engagementId = 0 }: BurndownChartProps) {
  const { formatMoney, formatCurrencyShort } = useFormatters()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('monthly')

  // Aggregate data based on selected time period
  const aggregatedData = useMemo(() => {
    return aggregateDataByTimePeriod(data, timePeriod)
  }, [data, timePeriod])

  // Transform data for burndown visualization with running totals
  let cumulativeCosts = 0
  let cumulativeBilled = 0
  const chartData = aggregatedData.map((item, index) => {
    const totalPeriods = aggregatedData.length
    
    // Calculate linear decrease for contract and budget (downward slope)
    // Formula: startValue * (totalPeriods - currentIndex) / totalPeriods
    // This creates a straight line from 100% at period 0 declining proportionally each period
    const plannedContractRemaining = contractAmount * (totalPeriods - index) / totalPeriods
    const plannedBudgetRemaining = projectedCost * (totalPeriods - index) / totalPeriods
    
    // Use cumulative values from aggregated data
    cumulativeCosts += item.costs
    cumulativeBilled += (item.billed || 0)
    
    return {
      period: item.period,
      // Carried for exports only (MM-DD-YYYY Period column); the axis uses `period`.
      periodDate: item.periodDate,
      contractAmount: plannedContractRemaining,
      projectedCost: plannedBudgetRemaining,
      actualCosts: cumulativeCosts,
      actualBilling: cumulativeBilled,
    }
  })

  const chartConfig = {
    contractAmount: {
      label: "Contract Amount",
    },
    projectedCost: {
      label: "Projected Cost",
    },
    actualCosts: {
      label: "Actual Costs",
    },
    actualBilling: {
      label: "Actual Billing",
    },
  } satisfies ChartConfig

  const handleExportCSV = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Contract Amount': item.contractAmount,
      'Projected Cost': item.projectedCost,
      'Actual Costs': item.actualCosts,
      'Actual Billing': item.actualBilling,
    }))
    exportToCSV(exportData, generateFilename('Burndown', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Contract Amount': item.contractAmount,
      'Projected Cost': item.projectedCost,
      'Actual Costs': item.actualCosts,
      'Actual Billing': item.actualBilling,
    }))
    exportToExcel(exportData, generateFilename('Burndown', engagementId, 'xlsx'), 'Burndown')
  }

  const handleExportPNG = () => {
    exportToPNG('burndown-chart', generateFilename('Burndown', engagementId, 'png'))
  }

  const hasData = aggregatedData && aggregatedData.length > 0

  return (
    <Card id="burndown-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Contract & Budget Summary</CardTitle>
            <CardDescription>Budget burndown over time</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
            <ChartExportMenu 
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        ) : (
        <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrencyShort}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  valueFormatter={(value) => formatMoney(Number(value))}
                />
              }
            />
            <ChartLegend />
            <Line 
              type="monotone" 
              dataKey="contractAmount" 
              stroke="#94a3b8" 
              strokeWidth={2}
              name="Contract Amount"
              strokeDasharray="5 5"
            />
            <Line 
              type="monotone" 
              dataKey="projectedCost" 
              stroke="#8b5cf6" 
              strokeWidth={2}
              name="Projected Cost"
              strokeDasharray="5 5"
            />
            <Line 
              type="monotone" 
              dataKey="actualCosts" 
              stroke="#ef4444" 
              strokeWidth={2}
              name="Actual Costs"
            />
            <Line 
              type="monotone" 
              dataKey="actualBilling" 
              stroke="#22c55e" 
              strokeWidth={2}
              name="Actual Billing"
            />
          </LineChart>
        </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

